import React, { useState } from 'react';
import {
  LoadingSpinner,
  ErrorMessage,
  ConfirmDialog
} from '@/shared/components';
import { FileUploadDropzone } from '@/shared/ui';
import { doctorsApi } from '@/shared/api';
import { FILE_UPLOAD_CONFIG } from '@/shared/config/api';
import type { DoctorWithImages, DoctorImage } from '@/shared/types';

/**
 * Пропсы для компонента DoctorImageManager
 */
interface DoctorImageManagerProps {
  doctor: DoctorWithImages;
  onUploadSuccess: () => void;
  onDeleteSuccess: () => void;
  onSetActiveSuccess: () => void;
}

/**
 * Компонент управления изображениями врача
 * Позволяет загружать, удалять и устанавливать активные изображения
 * @param doctor - Данные врача с изображениями
 * @param onUploadSuccess - Функция вызываемая при успешной загрузке
 * @param onDeleteSuccess - Функция вызываемая при успешном удалении
 * @param onSetActiveSuccess - Функция вызываемая при успешной установке активного изображения
 * @returns JSX элемент
 */
export const DoctorImageManager: React.FC<DoctorImageManagerProps> = ({
  doctor,
  onUploadSuccess,
  onDeleteSuccess,
  onSetActiveSuccess
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    image: DoctorImage | null;
  }>({ isOpen: false, image: null });

  /**
   * Получение URL изображения для админ-панели
   * Использует прямой URL из Supabase Storage
   */
  const getImageUrl = (image: DoctorImage): string => {
    // Используем imageUrl напрямую из Supabase Storage
    if (image.imageUrl) {
      return image.imageUrl;
    }

    // Fallback для старых изображений с relativePath
    if (image.relativePath) {
      return `${import.meta.env.VITE_BACKEND_URL}${image.relativePath}`;
    }

    return '';
  };

  /**
   * Валидация файла изображения
   */
  const validateImageFile = (file: File): string | null => {
    if (file.size > FILE_UPLOAD_CONFIG.MAX_FILE_SIZE) {
      return 'Размер файла не должен превышать 5MB';
    }

    if (
      !FILE_UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES.includes(
        file.type as (typeof FILE_UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES)[number]
      )
    ) {
      return 'Разрешены только файлы JPG, PNG и WebP';
    }

    return null;
  };

  /**
   * Обработчик выбора файлов из dropzone
   */
  const handleFileSelect = (files: File[]): void => {
    const file = files[0];
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    handleFileUpload(file);
  };

  /**
   * Обработчик загрузки файла
   */
  const handleFileUpload = async (file: File): Promise<void> => {
    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      await doctorsApi.uploadImage(
        doctor.id,
        file,
        (progressEvent: { loaded: number; total?: number }) => {
          const progress = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        }
      );

      onUploadSuccess();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' &&
        error.response.data !== null
          ? (error.response.data as { message?: string; error?: string })
              .message ||
            (error.response.data as { message?: string; error?: string })
              .error ||
            'Ошибка загрузки изображения'
          : error instanceof Error
            ? error.message
            : 'Ошибка загрузки изображения';
      setError(errorMessage);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Обработчик установки основного изображения
   */
  const handleSetPrimaryImage = async (image: DoctorImage): Promise<void> => {
    try {
      await doctorsApi.setActiveImage(doctor.id, image.id);
      onSetActiveSuccess();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' &&
        error.response.data !== null
          ? (error.response.data as { message?: string; error?: string })
              .message ||
            (error.response.data as { message?: string; error?: string })
              .error ||
            'Ошибка установки основного изображения'
          : error instanceof Error
            ? error.message
            : 'Ошибка установки основного изображения';
      setError(errorMessage);
    }
  };
  const handleUnsetPrimaryImage = async (): Promise<void> => {
    try {
      await doctorsApi.update(doctor.id, { doctorImageId: null });
      onSetActiveSuccess();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' &&
        error.response.data !== null
          ? (error.response.data as { message?: string; error?: string }).message ||
            (error.response.data as { message?: string; error?: string }).error ||
            'Ошибка снятия статуса основного изображения'
          : error instanceof Error
            ? error.message
            : 'Ошибка снятия статуса основного изображения';
      setError(errorMessage);
    }
  };


  /**
   * Открытие диалога удаления
   */
  const openDeleteDialog = (image: DoctorImage): void => {
    setDeleteDialog({ isOpen: true, image });
  };

  /**
   * Закрытие диалога удаления
   */
  const closeDeleteDialog = (): void => {
    setDeleteDialog({ isOpen: false, image: null });
  };

  /**
   * Подтверждение удаления изображения
   */
  const confirmDelete = async (): Promise<void> => {
    if (!deleteDialog.image) return;

    try {
      await doctorsApi.deleteImage(doctor.id, deleteDialog.image.id);
      onDeleteSuccess();
      closeDeleteDialog();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error &&
        'response' in error &&
        typeof error.response === 'object' &&
        error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' &&
        error.response.data !== null
          ? (error.response.data as { message?: string; error?: string })
              .message ||
            (error.response.data as { message?: string; error?: string })
              .error ||
            'Ошибка удаления изображения'
          : error instanceof Error
            ? error.message
            : 'Ошибка удаления изображения';
      setError(errorMessage);
      closeDeleteDialog();
    }
  };

  return (
    <div className="card card-bordered bg-base-100 shadow-sm">
      <h3 className="text-lg font-semibold text-base-content mb-4">
        <i className="fas fa-images mr-2 text-primary"></i>
        Управление изображениями
      </h3>

      {/* Сообщение об ошибке */}
      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      {/* Загрузка нового изображения */}
      <div className="mb-6">
        <FileUploadDropzone
          onFileSelect={handleFileSelect}
          accept="image/jpeg,image/jpg,image/png,image/webp"
          multiple={false}
          maxSize={FILE_UPLOAD_CONFIG.MAX_FILE_SIZE}
          disabled={isUploading}
          dropzoneText="Перетащите изображение сюда или нажмите для выбора"
          helpText="Разрешены файлы JPG, PNG, WebP. Максимальный размер: 5MB"
        />

        {isUploading && (
          <div className="flex items-center justify-center gap-2 mt-4 p-3 bg-primary/10 rounded-lg">
            <LoadingSpinner size="sm" />
            <span className="text-sm text-primary font-medium">
              Загрузка... {uploadProgress}%
            </span>
          </div>
        )}
      </div>

      {/* Список изображений */}
      {!doctor.images?.length ? (
        <div className="text-center py-8 border-2 border-dashed border-base-300 rounded-lg">
          <i className="fas fa-image text-4xl text-base-content/30 mb-4"></i>
          <p className="text-base-content/60">Изображения не загружены</p>
          <p className="text-sm text-base-content/40 mt-1">
            Загрузите первое изображение врача
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {doctor.images?.map((image: DoctorImage) => (
            <div
              key={image.id}
              className={`relative group border-2 rounded-lg overflow-hidden transition-all duration-200 ${
                doctor.doctorImageId === image.id
                  ? 'border-primary shadow-lg'
                  : 'border-base-300 hover:border-primary/50'
              }`}
            >
              {/* Изображение */}
              <div className="aspect-square bg-base-200">
                <img
                  src={getImageUrl(image)}
                  alt={`${doctor.fullName} - Изображение ${image.id}`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Оверлей с действиями */}
              <div className="absolute inset-0 z-20 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2 pointer-events-none group-hover:pointer-events-auto">
                {doctor.doctorImageId !== image.id ? (
                  <button
                    onClick={() => handleSetPrimaryImage(image)}
                    className="btn btn-sm btn-primary"
                    title="Сделать основным"
                  >
                    <i className="fas fa-star"></i>
                  </button>
                ) : (
                  <button
                    onClick={handleUnsetPrimaryImage}
                    className="btn btn-sm btn-warning"
                    title="Снять как основное"
                  >
                    <i className="fas fa-star-half-alt"></i>
                  </button>
                )}

                <button
                  onClick={() => openDeleteDialog(image)}
                  className="btn btn-sm btn-error"
                  title="Удалить"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>

              {/* Индикатор основного изображения */}
              {doctor.doctorImageId === image.id && (
                <div className="absolute top-2 left-2">
                  <span className="badge badge-primary badge-sm">
                    <i className="fas fa-star mr-1"></i>
                    Основное
                  </span>
                </div>
              )}

              {/* Информация об изображении */}
              <div className="absolute bottom-0 left-0 right-0 z-10 bg-black/70 text-white p-2">
                <p className="text-xs truncate">Изображение {image.id}</p>
                <p className="text-xs text-white/70">
                  {new Date(image.createdAt).toLocaleDateString('ru-RU')}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Диалог подтверждения удаления */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title="Удалить изображение"
        message={`Вы уверены, что хотите удалить изображение ${deleteDialog.image?.id}? Это действие нельзя отменить.`}
        confirmText="Удалить"
        cancelText="Отмена"
        onConfirm={confirmDelete}
        onCancel={closeDeleteDialog}
      />
    </div>
  );
};
